/**
 * Notifications Settings Page
 *
 * Manage workspace-wide email notification preferences.
 * Owner-only - controls which emails are sent to all workspace members.
 */

import { AlertCircle, Bell } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
} from '@loader/ui';

import { SettingsPageLayout } from '../components';
import { useNotificationPreferences } from './hooks/useNotificationPreferences';

import { useWorkspaceMembership } from '@/hooks';

interface NotificationItemProps {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: () => void;
  disabled?: boolean;
}

function NotificationItem({
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
}: NotificationItemProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="space-y-0.5">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

export function NotificationsPage() {
  const { isOwner, isLoading: isMembershipLoading } = useWorkspaceMembership();
  const {
    preferences,
    isLoading,
    isError,
    isSaving,
    togglePreference,
  } = useNotificationPreferences();

  return (
    <SettingsPageLayout
      title="Notifications"
      description="Control which email notifications are sent to workspace members"
      isLoading={isLoading || isMembershipLoading}
      isOwner={isOwner}
      requiresOwner
    >
      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load notification preferences. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Configure which emails you receive about workspace events
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {/* Collaboration Section */}
          <div className="pb-4">
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">Collaboration</h4>
            <NotificationItem
              title="Workspace Invitations"
              description="Receive an email when you're invited to a workspace"
              checked={preferences.workspace_invitation}
              onCheckedChange={() => togglePreference('workspace_invitation')}
              disabled={isSaving}
            />
          </div>

          {/* Data Changes Section */}
          <div className="pt-4">
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">Data Changes</h4>
            <div className="space-y-1 divide-y">
              <NotificationItem
                title="Current Version Changed"
                description="Notify when the current version tag changes on a table"
                checked={preferences.system_tag_changed}
                onCheckedChange={() => togglePreference('system_tag_changed')}
                disabled={isSaving}
              />
              <NotificationItem
                title="Version Deleted"
                description="Notify when a version is deleted from a table"
                checked={preferences.version_deleted}
                onCheckedChange={() => togglePreference('version_deleted')}
                disabled={isSaving}
              />
              <NotificationItem
                title="Table Deleted"
                description="Notify when a table is deleted from the workspace"
                checked={preferences.table_deleted}
                onCheckedChange={() => togglePreference('table_deleted')}
                disabled={isSaving}
              />
              <NotificationItem
                title="Linked Table Deleted"
                description="Notify when a linked table is deleted from the workspace"
                checked={preferences.linked_table_deleted}
                onCheckedChange={() => togglePreference('linked_table_deleted')}
                disabled={isSaving}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      )}
    </SettingsPageLayout>
  );
}
